import { createSelector } from 'reselect'
import { createAction, createReducer } from 'redux-starter-kit'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import { sharedReportApi, segmentApi } from '@edulastic/api'
import { notification } from '@edulastic/common'

import {
  buildNormalReportUrl,
  hasDroppedFilters,
  newSharedReportOpenState,
  writeSharedReportSource,
  clearSharedReportSource,
} from './utils'
import {
  getDsDefaultFiltersSelector,
  getOrgDataSelector,
} from '../../../../src/selectors/user'

const OPEN_SHARED_REPORT_AS_NORMAL =
  '[reports] open shared report as normal report'
const OPEN_SHARED_REPORT_AS_NORMAL_DONE =
  '[reports] open shared report as normal report done'
const SET_SHARED_REPORT_BLOCKED = '[reports] set shared report not available'
const CLEAR_SHARED_REPORT_BLOCKED =
  '[reports] clear shared report not available'
const SET_SHARED_VIEW_LOADING = '[reports] set shared view loading'

// -----|-----|-----|-----| ACTIONS BEGIN |-----|-----|-----|----- //

/**
 * payload: { sharedReport, history, trigger: 'open' | 'customize' | 'reset' | 'redirect' }
 */
export const openSharedReportAsNormalAction = createAction(
  OPEN_SHARED_REPORT_AS_NORMAL
)
const openSharedReportAsNormalDoneAction = createAction(
  OPEN_SHARED_REPORT_AS_NORMAL_DONE
)
const setSharedReportBlockedAction = createAction(SET_SHARED_REPORT_BLOCKED)
export const clearSharedReportBlockedAction = createAction(
  CLEAR_SHARED_REPORT_BLOCKED
)
/**
 * payload: { source, loading } — one part of the mounted report (its filters,
 * or its data) is still loading (see useSharedViewLoading)
 */
export const setSharedViewLoadingAction = createAction(SET_SHARED_VIEW_LOADING)

// -----|-----|-----|-----| ACTIONS ENDED |-----|-----|-----|----- //

// -----|-----|-----|-----| SELECTORS BEGIN |-----|-----|-----|----- //

const stateSelector = (state) =>
  state.reportReducer.sharedReportNavigationReducer

export const getOpeningSharedReport = createSelector(
  stateSelector,
  (state) => state.opening
)

/** { reportId, title, dataVisibility, trigger } when the share can't open for the viewer */
export const getSharedReportBlocked = createSelector(
  stateSelector,
  (state) => state.blocked
)

/** Share whose "their data" redirect failed; it falls back to the shared view. */
export const getFailedSharedReportId = createSelector(
  stateSelector,
  (state) => state.failedReportId
)

/** Some part of the mounted report (its filters or its data) is still loading. */
export const getSharedViewLoading = createSelector(
  stateSelector,
  (state) => Object.keys(state.sharedViewLoading).length > 0
)

// -----|-----|-----|-----| SELECTORS ENDED |-----|-----|-----|----- //

// -----|-----|-----|-----| REDUCER BEGIN |-----|-----|-----|----- //

const initialState = {
  opening: false,
  blocked: null,
  failedReportId: null,
  // sources still loading, keyed by source: the report container's filters
  // and the report's own data load apart
  sharedViewLoading: {},
}

export const sharedReportNavigationReducer = createReducer(initialState, {
  [OPEN_SHARED_REPORT_AS_NORMAL]: (state) => {
    state.opening = true
    state.blocked = null
  },
  [OPEN_SHARED_REPORT_AS_NORMAL_DONE]: (state, { payload = {} }) => {
    state.opening = false
    if (payload.failedReportId) {
      state.failedReportId = payload.failedReportId
    } else if (payload.navigated) {
      state.failedReportId = null
    }
  },
  [SET_SHARED_REPORT_BLOCKED]: (state, { payload }) => {
    state.opening = false
    state.blocked = payload
  },
  [CLEAR_SHARED_REPORT_BLOCKED]: (state) => {
    state.blocked = null
  },
  [SET_SHARED_VIEW_LOADING]: (state, { payload: { source, loading } }) => {
    if (loading) {
      state.sharedViewLoading[source] = true
    } else {
      delete state.sharedViewLoading[source]
    }
  },
})

// -----|-----|-----|-----| REDUCER ENDED |-----|-----|-----|----- //

// -----|-----|-----|-----| SAGAS BEGIN |-----|-----|-----|----- //

const SHARED_REPORTS_URL = '/author/reports/shared-reports'

const trackEvent = (event, properties) => {
  try {
    segmentApi.genericEventTrack(event, properties)
  } catch (e) {
    // analytics must never block navigation
  }
}

function* openSharedReportAsNormalSaga({ payload }) {
  const { sharedReport, history, trigger = 'open' } = payload
  try {
    const resolved = yield call(sharedReportApi.getViewerFilters, {
      id: sharedReport._id,
    })
    trackEvent('SharedReport_OpenAsReport', {
      reportId: resolved.reportId,
      reportType: resolved.reportType,
      dataVisibility: resolved.dataVisibility,
      trigger,
      removedKeys: (resolved.removed || []).map(({ key }) => key),
      blocked: !!resolved.blocked,
    })

    // an org/entity filter lost every value, or the test/student isn't the
    // viewer's: show "not available" instead of opening the report (HLD §4.3)
    if (resolved.blocked) {
      yield put(
        setSharedReportBlockedAction({
          reportId: resolved.reportId,
          title: resolved.title,
          dataVisibility: resolved.dataVisibility,
          trigger,
        })
      )
      return
    }

    // source banner on the normal report, with Reset to Shared Filters
    writeSharedReportSource({
      sourceReportId: resolved.reportId,
      reportType: resolved.reportType,
      title: resolved.title,
      sharedByName: resolved.sharedBy?.name,
      dataVisibility: resolved.dataVisibility,
      hasRemovedValues: hasDroppedFilters(resolved),
    })
    const orgData = yield select(getOrgDataSelector)
    const dsDefaultFilters = yield select(getDsDefaultFiltersSelector)
    const url = buildNormalReportUrl(resolved, {
      defaultTermId: orgData?.defaultTermId,
      dsDefaultFilters,
    })
    // the open marker remounts the report routes in the same render as the
    // navigation, so the report loads once with the shared filters
    const openState = newSharedReportOpenState()
    // reset/redirect replace the entry so Back doesn't return to the shared view
    if (trigger === 'reset' || trigger === 'redirect') {
      history.replace(url, openState)
    } else {
      history.push(url, openState)
    }
    yield put(openSharedReportAsNormalDoneAction({ navigated: true }))
  } catch (error) {
    yield put(
      openSharedReportAsNormalDoneAction({
        failedReportId: trigger === 'redirect' ? sharedReport._id : null,
      })
    )
    if (error?.status === 403 || error?.response?.status === 403) {
      // revoked or no longer shared with the viewer
      clearSharedReportSource()
      notification({ msg: 'You no longer have access to this shared report.' })
      history.push(SHARED_REPORTS_URL, { refreshSharedReports: true })
      return
    }
    notification({ msg: 'Unable to open the shared report. Please try again.' })
  }
}

export function* sharedReportNavigationSaga() {
  yield takeLatest(OPEN_SHARED_REPORT_AS_NORMAL, openSharedReportAsNormalSaga)
}

// -----|-----|-----|-----| SAGAS ENDED |-----|-----|-----|----- //
